import React from 'react';
import { Heart, Target, Award, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Purpose Over Profit",
      description: "Every bottle represents transformation and discipline. We craft with intention, not just for sales."
    },
    {
      icon: Target,
      title: "No Compromises",
      description: "High heat balanced with real flavor. We never sacrifice quality for quantity or cut corners on ingredients."
    },
    {
      icon: Award,
      title: "Craft Excellence",
      description: "From seed to sauce, every step is hands-on. We grow our peppers, test batches, and bottle by hand."
    },
    {
      icon: Users,
      title: "Community First",
      description: "Built for people who appreciate the endorphin rush and respect the craft behind extreme heat."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-black mb-6">
            Heat With <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">Purpose</span>
          </h1>
          <p className="text-2xl text-gray-300 leading-relaxed">
            More than hot sauce. This is about transformation, discipline, and channeling intensity into something meaningful.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert prose-lg max-w-none">
            <h2 className="text-4xl font-black mb-8 text-white">The Story Behind HHHS</h2>
            
            <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
              <p>
                HHHotsauce didn't start in a kitchen. It started with a search for something real — an authentic experience that demanded respect and delivered transformation.
              </p>

              <p>
                I discovered the endorphin rush of extreme heat years ago when a friend handed me a bottle of homemade sauce made with Carolina Reapers and Ghost Peppers. That first taste was unlike anything I'd experienced. It wasn't just heat for the sake of heat. There was balance — vinegar, garlic, fruit undertones that made the burn worth it. The intensity triggered something primal, a natural high that demanded focus and presence.
              </p>

              <p>
                But this isn't a story about chasing highs without consequence. I'm a grateful recovering addict. I know what it means to chase intensity in destructive ways, to lose control, and to fight back for something better. Recovery taught me discipline, honesty, and the power of channeling energy into purpose rather than escape.
              </p>

              <p>
                Crafting hot sauce became that channel. Growing peppers from seed, experimenting with flavor profiles, refining batches until they hit that perfect balance of pain and pleasure — it's meditative. It's earned. Every bottle of HHHotsauce represents hours of cultivation, testing, and refinement. It's not mass-produced. It's not rushed. It's intentional.
              </p>

              <p>
                This brand exists for people who understand that real satisfaction comes from respect — respect for the craft, for the process, and for the experience. HHHotsauce brings high heat, but it never skips out on flavor. We use Carolina Reapers and Ghost Peppers, but we balance them with fruit, vinegar, honey, and spices. The result is complex, layered, and memorable.
              </p>

              <p>
                Each batch is limited edition. Once it's sold out, it's gone forever. No repeats. No mass production. Just small-batch craft that honors the work, the ingredients, and the people who seek the endorphin rush with intention.
              </p>

              <p className="text-xl font-bold text-orange-400 border-l-4 border-orange-500 pl-6 my-8">
                HHHotsauce isn't just hot. It's earned.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-black text-center mb-12">
            What We <span className="text-orange-500">Stand For</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2 text-white">{value.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-black text-center mb-12">
            Our <span className="text-red-500">Process</span>
          </h2>
          
          <div className="space-y-8">
            {[
              {
                step: "01",
                title: "Seed Selection",
                description: "We start with premium pepper seeds — Carolina Reapers, Ghost Peppers, and specialty varieties. Quality begins at the source."
              },
              {
                step: "02",
                title: "Cultivation",
                description: "Every pepper is grown with care. We monitor soil, sunlight, and water to ensure peak capsaicin levels and flavor development."
              },
              {
                step: "03",
                title: "Harvest & Preparation",
                description: "Peppers are harvested at peak ripeness. We blend them with fruits, vinegar, honey, and spices to create complex flavor profiles."
              },
              {
                step: "04",
                title: "Testing & Refinement",
                description: "Each batch is tested extensively. We refine until the heat and flavor hit the perfect balance — intense but intentional."
              },
              {
                step: "05",
                title: "Small Batch Production",
                description: "We bottle by hand in limited quantities. Once a batch sells out, the recipe is retired forever. No repeats."
              }
            ].map((item, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
                    <span className="text-2xl font-black">{item.step}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_hhh-craft-heat/artifacts/mmf9qjjm_logo.png"
              alt="HHHS Logo"
              className="w-40 h-40 mx-auto opacity-80"
            />
          </div>
          <p className="text-2xl text-gray-300 leading-relaxed mb-8">
            We're not here to compete with mass-market brands. We're here for the people who understand that intensity, when channeled with purpose, becomes something powerful.
          </p>
          <p className="text-xl font-bold text-orange-400">
            Welcome to HHHotsauce. Heat with purpose. Flavor without compromise.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
